import { ChangeDetectorRef, Component, ComponentRef, OnInit, ViewChild, inject } from '@angular/core';

import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import {
    FavoriteButtonComponent,
    LoadingSkeletonComponent,
    LoadMoreButtonComponent,
    SubpageHeaderComponent,
} from '@components';
import { FavoriteId, HomeSectionLabel } from '@constants';
import { FAVORITE_DEFINITION_MAP, FavoritesService, Product, ProductService } from '@services';
import {
    ToastColor,
    ToastNotificationService,
} from '../../@shared/services/toast-notification/toast-notification.service';
import { Favorite } from '@types';
import { catchError, map, Observable, of, startWith, tap } from 'rxjs';

@Component({
    selector: 'app-home-favorites',
    imports: [
        CommonModule,
        FavoriteButtonComponent,
        LoadMoreButtonComponent,
        LoadingSkeletonComponent,
        MatIconModule,
        MatTooltipModule,
        RouterLink,
        SubpageHeaderComponent,
    ],
    templateUrl: './home-favorites.component.html',
    styleUrls: ['./home-favorites.component.scss'],
})
export class HomeFavoritesComponent implements OnInit {
    private cdr = inject(ChangeDetectorRef);
    private productService = inject(ProductService);
    private favoritesService = inject(FavoritesService);
    private toastService = inject(ToastNotificationService);

    /** h4 text for this section */
    title = HomeSectionLabel.FAVORITES;
    /** ref to the subsection header */
    @ViewChild('favoritesHeaderRef') headerRef: ComponentRef<SubpageHeaderComponent>;
    /** currently selected product */
    selectedProduct$: Observable<Product>;
    /** list of user's favorites from remote database */
    favorites$: Observable<Favorite[]>;
    /** number of items to show */
    length = 5;
    /** max number of items to show  */
    max: number;
    /** modifier to apply to {@link length} when load more button is clicked */
    modifier = 3;
    /** whether or not the load more button is disabled */
    isLoadMoreDisabled = false;
    /** array to loop over during loading state to build loading skeletons  */
    loadingArray = Array(this.length).fill(0);
    isLoaded$: Observable<boolean>;

    ngOnInit(): void {
        this.selectedProduct$ = this.productService.selectedProduct$;

        this.isLoaded$ = this.favoritesService.favorites$.pipe(
            map(() => true),
            startWith(false),
            catchError(() => of(true)),
        );

        this.favorites$ = this.favoritesService.favorites$.pipe(
            startWith([]),
            tap((favorites) => {
                this.max = favorites?.length || 0;
                this.isLoadMoreDisabled = (favorites?.length || 0) <= this.length;
                console.debug('favorites', favorites);
            }),
            catchError((error) => {
                const message = 'Error loading favorites: ' + (error?.message ?? error);
                console.error(message, error);
                this.toastService.notify({ innerHtml: message, color: ToastColor.CRITICAL });
                return of([]);
            }),
        );
    }

    /**
     * Return favorite label
     * @param favorite - Favorite item
     * @returns favorte label string
     */
    getLabel(favorite: Favorite): string {
        return favorite.label || FAVORITE_DEFINITION_MAP[favorite.favoriteId]?.label || '';
    }

    /**
     * Remove favorite by a given favorite id
     * @param favoriteId
     */
    remove(favoriteId: FavoriteId): void {
        this.favoritesService.remove(favoriteId).catch((error) => {
            console.error('Error removing favorite:', error);
        });
    }

    /**
     * Refresh loading length
     * @param val - number to load
     */
    refreshLength(val: number): void {
        this.length = val;
        this.cdr.detectChanges();
    }

    /*
     * Track by Item
     * @param _index - item index
     * @param item - any data
     * @returns number or string unique identifier
     */
    trackByItem(index: number, _data: FavoriteId): NonNullable<number | string> {
        return _data;
    }
}
